import classNames from "classnames";
import type { MapMouseEvent, StyleSpecification } from "maplibre-gl";
import * as maplibregl from "maplibre-gl";
import { usePostHog } from "posthog-js/react";
import React from "react";
import type { MapRef, ViewState } from "react-map-gl/maplibre";
import ReactMapGL, {
	FullscreenControl,
	NavigationControl,
} from "react-map-gl/maplibre";
import { LAYER_ORDER } from "../constants/layerOrder";
import { MapImage } from "../MapImage";
import { cwpStore, distanceLineStore, mapViewportStore } from "../state";
import Aircrafts from "./Aircrafts";
import Airways from "./Airways";
import CenterTextOverlay from "./CenterTextOverlay";
import DistanceMarkers from "./DistanceMarkers";
import DistanceMeasurements from "./DistanceMeasurements";
import EmptyLayer from "./EmptyLayer";
import FixesPoints from "./FixesPoints";
import FlightRoutes from "./FlightRoutes";
import HighlightedAircraft from "./HighlightedAircraft";
import LimboAircrafts from "./LimboAircrafts";
import NextFixPreviewLine from "./NextFixPreviewLine";
import PopupLines from "./PopupLines";
import QdmLabelPopups from "./QdmLabelPopups";
import Sectors from "./Sectors";
import SepLabelPopups from "./SepLabelPopups";
import SepQdmCurrentLine from "./SepQdmCurrentLine";
import SepQdmLines from "./SepQdmLines";
import SpeedVectors from "./SpeedVectors";
import TrajectoryPredictionLines from "./TrajectoryPredictionLines";
import ViewportPresetsControl from "./ViewportPresetsControl";

// Do not load the RTL plugin because it is unnecessary
// @ts-expect-error invalid type
// eslint-disable-next-line unicorn/prefer-top-level-await
maplibregl.setRTLTextPlugin("", () => {}, true).catch(() => {});

const mapStyle: StyleSpecification = {
	version: 8,
	name: "Minimal Country Borders",
	glyphs: "/map-fonts/{fontstack}/{range}.pbf",
	sources: {
		countries: {
			type: "geojson",
			data: "/countries.geojson",
		},
	},
	layers: [
		{
			id: "background",
			type: "background",
			paint: {
				"background-color": "#464646",
			},
		},
		{
			id: "country-borders",
			type: "line",
			// filter: ["==", "$type", "Polygon"],
			source: "countries",
			paint: {
				"line-color": "#565656",
				"line-width": 2,
			},
		},
	],
};

const style: React.CSSProperties = {
	width: "100%",
	height: "100%",
	background: "gray",
};

const CANVAS_CONTEXT_ATTRIBUTES: WebGLContextAttributes = {
	// preserveDrawingBuffer is required to make map screenshots work with PostHog
	// But it has a performance impact, so we only enable it when PostHog is enabled
	preserveDrawingBuffer: !!import.meta.env.VITE_PUBLIC_POSTHOG_KEY,
};

const createMapClickHandler =
	(posthog: ReturnType<typeof usePostHog>) =>
	(event: MapMouseEvent): void => {
		const { currentDistanceColor } = cwpStore;
		const coordinates = event.lngLat;

		if (currentDistanceColor === "") {
			posthog?.capture("map_clicked", {
				position: { lat: coordinates.lat, lng: coordinates.lng },
				context: "general_map_click",
			});
		} else {
			distanceLineStore.newMarker({
				lat: coordinates.lat,
				lng: coordinates.lng,
				colour: currentDistanceColor,
			});
			const numberOfMarkersForColor =
				distanceLineStore.getNumberOfMarkersForColour(currentDistanceColor);

			posthog?.capture("distance_marker_placed", {
				marker_color: currentDistanceColor,
				marker_number: numberOfMarkersForColor,
				position: { lat: coordinates.lat, lng: coordinates.lng },
				is_second_marker: numberOfMarkersForColor >= 2,
			});

			if (numberOfMarkersForColor >= 2) {
				cwpStore.setCurrentDistanceColor("");
			}
		}
	};

const createMapRightClickHandler =
	(posthog: ReturnType<typeof usePostHog>) =>
	(event: MapMouseEvent): void => {
		// Close ATC Menu if it's open
		if (cwpStore.ATCMenuAircraftId !== "") {
			cwpStore.clearATCMenuAircraftId();
		}

		posthog?.capture("map_right_clicked", {
			position: { lat: event.lngLat.lat, lng: event.lngLat.lng },
			context: "general_map_right_click",
		});
	};

const initialViewState: Partial<ViewState> = {
	longitude: 7,
	latitude: 44,
	zoom: 7,
};

// Rough bounds of the area
// const maxBounds: maplibregl.LngLatBoundsLike = [
//   4, 11, 15, 70,
// ];

// biome-ignore lint/suspicious/noShadowRestrictedNames: Should change one day, but not today
export default function Map() {
	const posthog = usePostHog();
	const [isMoving, setIsMoving] = React.useState(false);
	const [isResizing, setIsResizing] = React.useState(false);
	const mapRef = React.useRef<MapRef>(null);
	// Track number of concurrent move operations to handle overlapping animations
	const moveCountRef = React.useRef(0);
	const resizeTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(
		null,
	);
	// const { isDragging } = useDragging();

	// Set map reference for viewport store when available
	React.useEffect(() => {
		if (mapRef.current) {
			mapViewportStore.setMapRef(mapRef.current);
			// Initialize viewport state immediately when map is ready
			mapViewportStore.updateViewportState();
		}
	}, []);

	// Also set map reference when map loads (onLoad callback)
	const onMapLoad = React.useCallback(() => {
		if (mapRef.current) {
			mapViewportStore.setMapRef(mapRef.current);
			mapViewportStore.updateViewportState();
		}
	}, []);

	// Disable animations briefly during window resize (debounced)
	React.useEffect(() => {
		const handleResize = (): void => {
			setIsResizing(true);
			if (resizeTimeoutRef.current) {
				clearTimeout(resizeTimeoutRef.current);
			}
			resizeTimeoutRef.current = setTimeout(() => {
				setIsResizing(false);
				resizeTimeoutRef.current = null;
			}, 250);
		};

		window.addEventListener("resize", handleResize);
		return () => {
			window.removeEventListener("resize", handleResize);
			if (resizeTimeoutRef.current) {
				clearTimeout(resizeTimeoutRef.current);
				resizeTimeoutRef.current = null;
			}
		};
	}, []);

	const onMoveStart = (): void => {
		moveCountRef.current += 1;
		// Only set isMoving to true on the first move start
		if (moveCountRef.current === 1) {
			setIsMoving(true);
			mapViewportStore.setIsMoving(true);
			posthog?.capture("map_move_start", {
				zoom: mapRef.current?.getZoom(),
				center: mapRef.current?.getCenter(),
			});
		}
	};
	const onMoveEnd = (): void => {
		moveCountRef.current = Math.max(0, moveCountRef.current - 1);
		// Only set isMoving to false when all moves have ended
		if (moveCountRef.current === 0) {
			setIsMoving(false);
			mapViewportStore.setIsMoving(false);
			// Update viewport store with debouncing to prevent excessive events
			mapViewportStore.debouncedUpdateViewportState(1500);

			const zoom = mapRef.current?.getZoom();
			const center = mapRef.current?.getCenter();
			posthog?.capture("map_move_end", {
				zoom: zoom,
				center: center ? { lat: center.lat, lng: center.lng } : null,
			});
		}
	};

	const onZoom = (): void => {
		// Update viewport store with debouncing for zoom changes
		mapViewportStore.debouncedUpdateViewportState(1000);

		const zoom = mapRef.current?.getZoom();
		posthog?.capture("map_zoom", {
			zoom_level: zoom,
		});
	};

	return (
		<div
			className={classNames(
				"radar-map-container grow",
				isMoving && "map-is-moving",
				isResizing && "map-is-resizing",
			)}
		>
			<ReactMapGL
				ref={mapRef}
				id="radar-map"
				style={style}
				initialViewState={initialViewState}
				// maxBounds={maxBounds}
				// mapStyle="https://basemaps.cartocdn.com/gl/dark-matter-nolabels-gl-style/style.json"
				// mapStyle="https://basemaps.cartocdn.com/gl/positron-gl-style/style.json"
				// mapStyle="https://maps.geoapify.com/v1/styles/dark-matter-dark-grey/style.json?apiKey=332569cc796d4788a11275c2fdefb5b1"
				mapStyle={mapStyle}
				attributionControl={false}
				mapLib={maplibregl}
				onClick={createMapClickHandler(posthog)}
				onContextMenu={createMapRightClickHandler(posthog)}
				onLoad={onMapLoad}
				onMoveStart={onMoveStart}
				onMoveEnd={onMoveEnd}
				onZoomEnd={onZoom}
				renderWorldCopies={false}
				// If rotation and pitch should be disabled:
				maxPitch={0}
				pitchWithRotate={false}
				dragRotate={false}
				canvasContextAttributes={CANVAS_CONTEXT_ATTRIBUTES}
			>
				{/* Map resources */}
				<MapImage url="/fixes.png" name="fixes" sdf={true} />

				{/* Layer ordering anchors - these empty layers establish z-order for dynamic layers */}
				{/* Order is bottom to top: layers with beforeId="X" appear BELOW layer X */}
				<EmptyLayer id={LAYER_ORDER.SECTOR_BACKGROUND} />
				<EmptyLayer id={LAYER_ORDER.SECTOR_POLYGONS} />
				<EmptyLayer id={LAYER_ORDER.AIRWAYS} />
				<EmptyLayer id={LAYER_ORDER.CENTER_TEXT} />
				<EmptyLayer id={LAYER_ORDER.FIXES} />
				<EmptyLayer id={LAYER_ORDER.FLIGHT_ROUTES} />
				<EmptyLayer id={LAYER_ORDER.SPEED_VECTORS} />
				<EmptyLayer id={LAYER_ORDER.TRAJECTORY_PREDICTION} />
				<EmptyLayer id={LAYER_ORDER.DISTANCE_MEASUREMENTS} />
				<EmptyLayer id={LAYER_ORDER.SEP_QDM_LINES} />
				<EmptyLayer id={LAYER_ORDER.INTERACTIVE_LINES} />

				{/* Controls */}
				<NavigationControl
					position="bottom-left"
					visualizePitch={false}
					showCompass={false}
				/>
				<FullscreenControl position="bottom-left" containerId="root" />
				<ViewportPresetsControl />

				{/* Background */}
				<Sectors beforeId={LAYER_ORDER.SECTOR_POLYGONS} />
				<Airways beforeId={LAYER_ORDER.AIRWAYS} />
				<CenterTextOverlay beforeId={LAYER_ORDER.CENTER_TEXT} />
				<FixesPoints beforeId={LAYER_ORDER.FIXES} />

				{/* Aircrafts layers */}
				<FlightRoutes beforeId={LAYER_ORDER.FLIGHT_ROUTES} />
				<Aircrafts />
				<HighlightedAircraft />
				<LimboAircrafts />

				{/* Future */}
				<SpeedVectors beforeId={LAYER_ORDER.SPEED_VECTORS} />
				<TrajectoryPredictionLines
					beforeId={LAYER_ORDER.TRAJECTORY_PREDICTION}
				/>
				<DistanceMarkers />
				<DistanceMeasurements beforeId={LAYER_ORDER.DISTANCE_MEASUREMENTS} />

				{/* Popups */}
				<PopupLines />
				<QdmLabelPopups />
				<SepLabelPopups />

				{/* Interactive lines */}
				<SepQdmLines beforeId={LAYER_ORDER.SEP_QDM_LINES} />
				<SepQdmCurrentLine beforeId={LAYER_ORDER.INTERACTIVE_LINES} />
				<NextFixPreviewLine beforeId={LAYER_ORDER.INTERACTIVE_LINES} />
			</ReactMapGL>
		</div>
	);
}
